import React, { useCallback, useMemo, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons/faChevronUp";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Collapse from "antd/es/collapse";
import { Col } from "antd/es/grid";
import Row from "antd/es/row";
import Tabs, { TabsProps } from "antd/es/tabs";
import { useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { EntityTypesSupportingDownloading } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { Button, CollapsePanel, Modal } from "@mapmycustomers/ui";

import ColumnActions from "@app/component/SelectColumnsPane/ColumnActions";
import DownloadEntitiesMapping from "@app/types/DownloadEntitiesMapping";
import ColumnVisibility from "@app/types/viewModel/ColumnVisibility";

import localSettings from "../../config/LocalSettings";
import layout from "../../styles/layout";
import getFieldModelByEntityType from "../../util/fieldModel/getByEntityType";
import ColumnSelector from "../input/ColumnSelector/ColumnSelector";

import Info from "./components/Info";
import styles from "./DownloadMultipleEntitiesModal.module.scss";
import messages from "./messages";

const onlyShowExportableColumns = (field: IField) =>
  !field.hasFeature(FieldFeature.NON_EXPORT_VIEW);

// Canonical tab order. Whatever order `visibleEntities` arrives in (which
// varies between map and route pages because each persists its own
// viewState blob), tabs render in a stable order with invisible types
// skipped. The Record is exhaustive over EntityTypesSupportingDownloading,
// so adding a new downloadable type is a compile error until ranked here.
const TAB_ORDER: Record<EntityTypesSupportingDownloading, number> = {
  [EntityType.ACTIVITY]: 3,
  [EntityType.COMPANY]: 0,
  [EntityType.DEAL]: 2,
  [EntityType.PERSON]: 1,
  [EntityType.TRIP]: 4,
};

interface DownloadMultipleEntitiesProps {
  alwaysShowEntityTabs?: boolean;
  configurationKey?: string;
  hasSorting?: boolean;
  hideInformation?: boolean;
  onDownload: (entityColumnsMap: DownloadEntitiesMapping) => void;
  onHide: () => void;
  visibleEntities: EntityTypesSupportingDownloading[];
}

const DownloadMultipleEntitiesModal = ({
  alwaysShowEntityTabs = false,
  configurationKey,
  hasSorting,
  hideInformation,
  onDownload,
  onHide,
  visibleEntities,
}: DownloadMultipleEntitiesProps) => {
  const intl = useIntl();

  const orderedEntities = useMemo(
    () => [...visibleEntities].sort((a, b) => TAB_ORDER[a] - TAB_ORDER[b]),
    [visibleEntities]
  );

  const [activeEntityType, setActiveEntityType] = useState<EntityTypesSupportingDownloading>(
    orderedEntities[0]
  );

  const [entityColumnsMap, setEntityColumnsMap] = useState<DownloadEntitiesMapping>(
    new Map(
      orderedEntities.map((visibleEntity) => {
        const configurationKeyToSave = `${visibleEntity}${configurationKey ?? ""}`;
        const savedConfiguration: IField["name"][] | undefined =
          localSettings.getExportEntityFields(configurationKeyToSave);
        return [
          visibleEntity,
          getFieldModelByEntityType(visibleEntity)
            .fields.filter(
              (field) => !savedConfiguration || savedConfiguration.includes(field.name)
            )
            .map((field) => ({ field, visible: true })),
        ];
      })
    )
  );

  const [columnVisible, setColumnVisibility] = useState<boolean>(true);

  const handleColumnChange = useCallback(
    (visibilities: ColumnVisibility[]) => {
      setEntityColumnsMap(
        (entityColumnsMap) => new Map(entityColumnsMap.set(activeEntityType, visibilities))
      );
    },
    [activeEntityType]
  );

  const handleDownload = useCallback(() => {
    orderedEntities.forEach((visibleEntity) => {
      const configurationKeyToSave = `${visibleEntity}${configurationKey ?? ""}`;
      localSettings.setExportEntityFields(
        configurationKeyToSave,
        entityColumnsMap
          .get(visibleEntity)!
          .filter(({ visible }) => visible)
          .map(({ field: { name } }) => name)
      );
    });
    onDownload(entityColumnsMap);
    onHide();
  }, [configurationKey, entityColumnsMap, onHide, onDownload, orderedEntities]);

  const handleTabClick = useCallback<NonNullable<TabsProps["onTabClick"]>>(
    (key) => setActiveEntityType(key as EntityTypesSupportingDownloading),
    []
  );

  return (
    <Modal
      centered
      footer={
        <Row gutter={layout.spacerM} justify="end">
          <Col>
            <Button onClick={onHide}>{intl.formatMessage(messages.cancel)}</Button>
          </Col>
          <Col>
            <Button
              icon={<FontAwesomeIcon icon={faDownload} />}
              onClick={handleDownload}
              type="primary"
            >
              {intl.formatMessage(messages.download)}
            </Button>
          </Col>
        </Row>
      }
      onCancel={onHide}
      open
      scrollable
      title={intl.formatMessage(messages.confirm)}
    >
      <div className={styles.container}>
        {!hideInformation && <Info hasSorting={hasSorting} />}
        <Collapse
          accordion
          activeKey={columnVisible ? "collapse" : undefined}
          className={styles.collapseContainer}
          ghost
          onChange={(key) => setColumnVisibility(!!key)}
        >
          <CollapsePanel
            extra={<FontAwesomeIcon icon={columnVisible ? faChevronDown : faChevronUp} />}
            header={intl.formatMessage(messages.text)}
            key="collapse"
            showArrow={false}
          >
            {orderedEntities.length > 1 || alwaysShowEntityTabs ? (
              <Tabs
                activeKey={activeEntityType}
                className={styles.tabs}
                onTabClick={handleTabClick}
              >
                {orderedEntities.map((key) => (
                  <Tabs.TabPane
                    key={key}
                    tab={intl.formatMessage(messages[key as EntityTypesSupportingDownloading])}
                  />
                ))}
              </Tabs>
            ) : undefined}
            <ColumnActions
              className={styles.actions}
              columns={entityColumnsMap.get(activeEntityType)!}
              entityType={activeEntityType}
              onUpdate={handleColumnChange}
            />
            <ColumnSelector
              columns={entityColumnsMap.get(activeEntityType)!}
              columnTreeClassName={styles.columnTree}
              entityType={activeEntityType}
              fieldFilter={onlyShowExportableColumns}
              onUpdate={handleColumnChange}
              showSelectAllLabel
            />
          </CollapsePanel>
        </Collapse>
      </div>
    </Modal>
  );
};

export default DownloadMultipleEntitiesModal;
